import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  ConsignmentAgreement,
  ConsignmentIntake,
  ConsignmentInventoryItem,
  Consignor,
  DynamicsCustomerSearchResult,
} from '@tcvs/shared';
import { useConsignmentApi } from './consignment-api';
import { ConsignmentCharges } from './ConsignmentCharges';
import { ConsignmentTerms } from './ConsignmentTerms';
import { ConsignmentStatements } from './ConsignmentStatements';
import { ConsignmentBookkeeping } from './ConsignmentBookkeeping';
import { batchHref, consignmentErrorMessage, formatMoneyMinor } from './consignment-ui';

type Section =
  | 'Consignors'
  | 'Terms'
  | 'Intake'
  | 'Inventory'
  | 'Charges'
  | 'Settlements'
  | 'Bookkeeping';
const sections: readonly Section[] = [
  'Consignors',
  'Terms',
  'Intake',
  'Inventory',
  'Charges',
  'Settlements',
  'Bookkeeping',
];

export function ConsignmentPage(): JSX.Element {
  const api = useConsignmentApi();
  const [section, setSection] = useState<Section>('Consignors');
  const [consignors, setConsignors] = useState<Consignor[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  const [agreements, setAgreements] = useState<ConsignmentAgreement[]>([]);
  const [intakes, setIntakes] = useState<ConsignmentIntake[]>([]);
  const [inventory, setInventory] = useState<ConsignmentInventoryItem[]>([]);
  const [inventoryCursor, setInventoryCursor] = useState<string>();
  const [inventoryLoadingMore, setInventoryLoadingMore] = useState(false);
  const [inventoryLoadingInitial, setInventoryLoadingInitial] = useState(false);
  const inventoryPage = useRef<{
    owner: string;
    cursor?: string;
    loading: boolean;
    continuation?: object;
  }>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);

  const loadConsignors = useCallback(async () => {
    const result = await api.listConsignors();
    if (!result.ok) return setError(consignmentErrorMessage(result));
    setConsignors(result.data.items);
    setSelectedId((current) => current || result.data.items[0]?.consignorId || '');
  }, [api]);

  const loadSelected = useCallback(async () => {
    if (selectedId === '') return;
    const page = { owner: selectedId, loading: true, cursor: undefined as string | undefined };
    inventoryPage.current = page;
    setInventoryCursor(undefined);
    setInventoryLoadingInitial(true);
    setInventoryLoadingMore(false);
    try {
      const [agreementResult, intakeResult, inventoryResult] = await Promise.all([
        api.listAgreements(selectedId),
        api.listIntakes(selectedId),
        api.listInventory(selectedId),
      ]);
      if (selectedRef.current !== selectedId || inventoryPage.current !== page) return;
      if (!agreementResult.ok) return setError(consignmentErrorMessage(agreementResult));
      if (!intakeResult.ok) return setError(consignmentErrorMessage(intakeResult));
      if (!inventoryResult.ok) return setError(consignmentErrorMessage(inventoryResult));
      setAgreements(agreementResult.data.items);
      setIntakes(intakeResult.data.items);
      setInventory(inventoryResult.data.items);
      page.cursor = inventoryResult.data.cursor;
      setInventoryCursor(page.cursor);
    } catch (caught) {
      if (selectedRef.current === selectedId && inventoryPage.current === page)
        setError(caught instanceof Error ? caught.message : 'Unable to load owned cards.');
    } finally {
      if (selectedRef.current === selectedId && inventoryPage.current === page) {
        page.loading = false;
        setInventoryLoadingInitial(false);
      }
    }
  }, [api, selectedId]);

  const loadMoreInventory = async () => {
    const page = inventoryPage.current;
    if (!page?.cursor || page.loading || page.continuation || page.owner !== selectedId) return;
    const continuation = {};
    page.continuation = continuation;
    setInventoryLoadingMore(true);
    setError(undefined);
    const isCurrent = () =>
      selectedRef.current === page.owner &&
      inventoryPage.current === page &&
      page.continuation === continuation;
    try {
      const result = await api.listInventory(page.owner, page.cursor);
      if (!isCurrent()) return;
      if (!result.ok) {
        setError(consignmentErrorMessage(result));
        return;
      }
      setInventory((current) => {
        const known = new Set(current.map((item) => item.cardId));
        const additions = result.data.items.filter((item) => {
          if (known.has(item.cardId)) return false;
          known.add(item.cardId);
          return true;
        });
        return [...current, ...additions];
      });
      page.cursor = result.data.cursor;
      setInventoryCursor(page.cursor);
    } catch (caught) {
      if (isCurrent())
        setError(caught instanceof Error ? caught.message : 'Unable to load more owned cards.');
    } finally {
      if (isCurrent()) {
        page.continuation = undefined;
        setInventoryLoadingMore(false);
      }
    }
  };

  useEffect(() => void loadConsignors(), [loadConsignors]);
  useEffect(() => void loadSelected(), [loadSelected, section]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'consignment_operation_failed');
    } finally {
      setBusy(false);
    }
  };

  const selected = consignors.find((item) => item.consignorId === selectedId);
  return (
    <div className="tcvs-consignment">
      <div className="tcvs-page-heading">
        <div>
          <h1>Consignment</h1>
          <p>Custody, terms, intake, and owned inventory</p>
        </div>
        <label className="tcvs-field tcvs-consignment-selector">
          <span>Consignor</span>
          <select
            value={selectedId}
            onChange={(event) => {
              setAgreements([]);
              setIntakes([]);
              setInventory([]);
              setInventoryCursor(undefined);
              setInventoryLoadingMore(false);
              setInventoryLoadingInitial(false);
              inventoryPage.current = undefined;
              selectedRef.current = event.target.value;
              setSelectedId(event.target.value);
            }}
          >
            <option value="">Select consignor</option>
            {consignors.map((item) => (
              <option key={item.consignorId} value={item.consignorId}>
                {item.displayName}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="tcvs-checklist-view-toggle" role="tablist">
        {sections.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={section === item}
            onClick={() => setSection(item)}
          >
            {item}
          </button>
        ))}
      </div>
      {error === undefined ? null : <p className="tcvs-error">Error: {error}</p>}
      {notice === undefined ? null : <p className="tcvs-success">{notice}</p>}
      {section === 'Consignors' ? (
        <ConsignorsPanel
          items={consignors}
          busy={busy}
          run={run}
          api={api}
          reload={loadConsignors}
          select={setSelectedId}
        />
      ) : null}
      {section === 'Terms' ? (
        selected ? (
          <ConsignmentTerms
            key={selectedId}
            consignorId={selectedId}
            consignorName={selected.displayName}
          />
        ) : (
          <p>Select a consignor.</p>
        )
      ) : null}
      {section === 'Intake' ? (
        <IntakePanel
          key={selectedId}
          consignor={selected}
          agreements={agreements}
          intakes={intakes}
          busy={busy}
          run={run}
          api={api}
          reload={loadSelected}
          notice={setNotice}
        />
      ) : null}
      {section === 'Inventory' ? (
        <InventoryPanel
          items={inventory}
          cursor={inventoryCursor}
          loadingMore={inventoryLoadingInitial || inventoryLoadingMore}
          loadMore={loadMoreInventory}
        />
      ) : null}
      {section === 'Charges' &&
        (selectedId ? (
          <ConsignmentCharges key={selectedId} consignorId={selectedId} />
        ) : (
          <p>Select a consignor.</p>
        ))}
      {section === 'Bookkeeping' ? (
        selectedId ? (
          <ConsignmentBookkeeping key={selectedId} consignorId={selectedId} />
        ) : (
          <p>Select a consignor.</p>
        )
      ) : null}
      {section === 'Settlements' ? (
        <SettlementsPanel
          consignor={selected}
          agreements={agreements}
          inventoryCount={inventory.length}
        />
      ) : null}
    </div>
  );
}

type Api = ReturnType<typeof useConsignmentApi>;
type Runner = (action: () => Promise<void>) => Promise<void>;

function ConsignorsPanel({
  items,
  busy,
  run,
  api,
  reload,
  select,
}: {
  items: Consignor[];
  busy: boolean;
  run: Runner;
  api: Api;
  reload: () => Promise<void>;
  select: (id: string) => void;
}): JSX.Element {
  const [results, setResults] = useState<DynamicsCustomerSearchResult[]>([]);
  const [searched, setSearched] = useState(false);

  const search = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void run(async () => {
      const result = await api.searchDynamicsCustomers(String(form.get('query') ?? ''));
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      setResults(result.data.items);
      setSearched(true);
    }).catch(() => undefined);
  };

  const link = (customer: DynamicsCustomerSearchResult) => {
    if (customer.linkedConsignorId !== undefined) {
      select(customer.linkedConsignorId);
      return;
    }
    void run(async () => {
      const result = await api.mutate<Consignor>({
        operation: 'LINK_DYNAMICS_CONSIGNOR',
        recordType: customer.recordType,
        recordId: customer.recordId,
      });
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      await reload();
      select(result.data.consignorId);
      setResults((current) =>
        current.map((item) =>
          item.recordId === customer.recordId && item.recordType === customer.recordType
            ? { ...item, linkedConsignorId: result.data.consignorId }
            : item,
        ),
      );
    }).catch(() => undefined);
  };

  return (
    <>
      <section className="tcvs-card">
        <h2>Find Dynamics customer</h2>
        <form className="tcvs-form-row" onSubmit={search}>
          <label className="tcvs-field tcvs-consignment-crm-search">
            <span>Name, email, or TCVS external ID</span>
            <input name="query" minLength={2} maxLength={120} required />
          </label>
          <button disabled={busy}>Search Dynamics</button>
        </form>
        {results.length === 0 ? (
          searched ? (
            <p className="tcvs-muted">No active Dynamics customers matched.</p>
          ) : null
        ) : (
          <div className="tcvs-table-wrap tcvs-consignment-crm-results">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>CRM type</th>
                  <th>Email</th>
                  <th>External ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((item) => (
                  <tr key={`${item.recordType}:${item.recordId}`}>
                    <td>{item.displayName}</td>
                    <td>{item.recordType === 'ACCOUNT' ? 'Account' : 'Contact'}</td>
                    <td>{item.email ?? 'Not provided'}</td>
                    <td>{item.externalId ?? 'Not provided'}</td>
                    <td>
                      <button disabled={busy} onClick={() => link(item)}>
                        {item.linkedConsignorId === undefined
                          ? 'Add consignor'
                          : 'Select consignor'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="tcvs-card">
        <h2>Consignors</h2>
        <div className="tcvs-table-wrap">
          <table className="tcvs-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>CRM</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.consignorId}>
                  <td>
                    <button className="tcvs-link-button" onClick={() => select(item.consignorId)}>
                      {item.displayName}
                    </button>
                  </td>
                  <td>{item.partyType}</td>
                  <td>{item.status}</td>
                  <td>{item.externalCrm === undefined ? 'Manual' : 'Dynamics 365'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function IntakePanel({
  consignor,
  agreements,
  intakes,
  busy,
  run,
  api,
  reload,
  notice,
}: {
  consignor: Consignor | undefined;
  agreements: ConsignmentAgreement[];
  intakes: ConsignmentIntake[];
  busy: boolean;
  run: Runner;
  api: Api;
  reload: () => Promise<void>;
  notice: (value: string) => void;
}): JSX.Element {
  if (consignor === undefined) return <p className="tcvs-muted">Select a consignor.</p>;
  const createIntake = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = event.currentTarget;
    const form = new FormData(target);
    void run(async () => {
      const result = await api.mutate<ConsignmentIntake>({
        operation: 'CREATE_INTAKE',
        consignorId: consignor.consignorId,
        agreementId: String(form.get('agreement') ?? ''),
        notes: String(form.get('notes') ?? ''),
      });
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      await reload();
      target.reset();
    });
  };
  const assign = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void run(async () => {
      const result = await api.assignBatch(
        String(form.get('intake') ?? ''),
        String(form.get('batch') ?? ''),
      );
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      notice(`${result.data.assigned} cards assigned; ${result.data.unchanged} unchanged.`);
      await reload();
    });
  };
  return (
    <div className="tcvs-consignment-grid">
      <section className="tcvs-card">
        <h2>Open intake</h2>
        <p>Prepare and publish an agreement in Terms before opening an intake.</p>
        <form className="tcvs-form-row" onSubmit={createIntake}>
          <label className="tcvs-field">
            <span>Terms</span>
            <select name="agreement" required>
              <option value="">Select terms</option>
              {agreements
                .filter(
                  (item) =>
                    item.status === 'ACTIVE' &&
                    (!item.feePolicy || item.feePolicy.selectedItemType === 'SINGLE_CARD'),
                )
                .map((item) => (
                  <option key={item.agreementId} value={item.agreementId}>
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="tcvs-field">
            <span>Notes</span>
            <input name="notes" />
          </label>
          <button disabled={busy}>Create intake</button>
        </form>
      </section>
      <section className="tcvs-card tcvs-consignment-wide">
        <h2>Assign batch</h2>
        <form className="tcvs-form-row" onSubmit={assign}>
          <label className="tcvs-field">
            <span>Intake</span>
            <select name="intake" required>
              <option value="">Select intake</option>
              {intakes
                .filter((item) => item.status === 'OPEN')
                .map((item) => (
                  <option key={item.intakeId} value={item.intakeId}>
                    {item.receivedAt.slice(0, 10)} · {item.cardCount} cards
                  </option>
                ))}
            </select>
          </label>
          <label className="tcvs-field tcvs-consignment-batch">
            <span>Storage batch ID</span>
            <input name="batch" required />
          </label>
          <button disabled={busy}>Assign batch</button>
        </form>
      </section>
    </div>
  );
}

function InventoryPanel({
  items,
  cursor,
  loadingMore,
  loadMore,
}: {
  items: ConsignmentInventoryItem[];
  cursor?: string;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
}): JSX.Element {
  return (
    <section className="tcvs-card">
      <h2>
        Owned inventory <span className="tcvs-muted">{items.length} loaded</span>
      </h2>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Card</th>
              <th>Identity</th>
              <th>Batch</th>
              <th>Price</th>
              <th>Assigned</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.cardId}>
                <td>
                  <Link to={`/catalog/${item.cardId}`}>{item.shortCode ?? item.cardId}</Link>
                </td>
                <td>{item.identityDisplay || 'Awaiting identification'}</td>
                <td>
                  {item.batchId === undefined ? (
                    'Not stored'
                  ) : (
                    <Link to={batchHref(item.batchId)}>{item.batchId.slice(-6)}</Link>
                  )}
                </td>
                <td>{formatMoneyMinor(item.listingPriceMinor)}</td>
                <td>{item.assignedAt.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor ? (
        <button disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? 'Loading owned cards…' : 'Load more owned cards'}
        </button>
      ) : null}
    </section>
  );
}

function SettlementsPanel({
  consignor,
  agreements,
  inventoryCount,
}: {
  consignor: Consignor | undefined;
  agreements: ConsignmentAgreement[];
  inventoryCount: number;
}): JSX.Element {
  return (
    <section>
      <h2>Settlements</h2>
      <dl className="tcvs-detail-list">
        <div>
          <dt>Consignor</dt>
          <dd>{consignor?.displayName ?? 'Not selected'}</dd>
        </div>
        <div>
          <dt>Active terms</dt>
          <dd>
            {
              agreements.filter(
                (item) =>
                  item.status === 'ACTIVE' &&
                  (!item.feePolicy || item.feePolicy.selectedItemType === 'SINGLE_CARD'),
              ).length
            }
          </dd>
        </div>
        <div>
          <dt>Owned cards loaded</dt>
          <dd>{inventoryCount}</dd>
        </div>
      </dl>
      {consignor ? (
        <ConsignmentStatements key={consignor.consignorId} consignorId={consignor.consignorId} />
      ) : (
        <p>Select a consignor.</p>
      )}
    </section>
  );
}
